import {Grid} from '@mui/material'
import {eachDayOfInterval, sub} from 'date-fns'
import {capitalize} from 'lodash'
import moment from 'moment'
import {useEffect, useState} from 'react'
import {useCompany} from 'src/contexts/CompanyContext'
import useAgeAndGenderCounting from 'src/hooks/api/personalized-dashboard/useAgeAndGenderCounting'
import useGetCountingFaces from 'src/hooks/api/personalized-dashboard/useGetCountingFaces'
import useGetHeatmapData from 'src/hooks/api/personalized-dashboard/useGetHeatmapData'
import useGetHistogramData from 'src/hooks/api/personalized-dashboard/useGetHistogramData'
import AgeIcon from 'src/images/AgeIcon'
import EmployeesDetectedIcon from 'src/images/EmployeesDetectedIcon'
import FaceDetectedIcon from 'src/images/FaceDetectedIcon'
import MenIcon from 'src/images/MenIcon'
import PersonDetectedIcon from 'src/images/PersonDetectedIcon'
import WomenIcon from 'src/images/WomenIcon'
import CountCardMoreInfos from 'src/pages/dashboard/components/CountCardMoreInfos'
import DashboardColumnAndLinesChartsCard from 'src/pages/dashboard/components/DashboardColumnAndLinesChartsCard'
import DashboardHeatMapTable, {ChartData} from 'src/pages/dashboard/components/DashboardHeatMapTable'
import {
  AGE_GROUPS_WITH_LEGENDS,
  calculateAverage,
  calculateIncreasePercentage,
  calculatePercent,
  formatWeekOnLabel,
  GenderEnum,
  showDateSubtitle,
  transformIntoPositiveNumber,
  weekOrder,
} from 'src/pages/dashboard/personalized-dashboards/functions'
import {abbreviateNumber} from 'src/utilities/formatter'
import {useWindowSize} from 'usehooks-ts'
import DashboardColumnChartsCard from '../components/ColumnChartCard'
import DashboardTableRowCard from '../components/TableRowCard'

type Props = {
  filter: {cameraIds: Array<number>; clientIds: Array<number>; start: string; end: string; typeHistogram?: string}
  onChangeFilter: (typeHistogram?: string) => void
}

export default function AnaliticDashboard({filter, onChangeFilter}: Props) {
  const {selectedCompanyId} = useCompany()
  const windowSize = useWindowSize()
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const offset = new Date().getTimezoneOffset() / 60

  const daysOnInterval = eachDayOfInterval({start: new Date(filter.start), end: new Date(filter.end)})
  const [daysFormatedToSubtitle, setDaysFormatedToSubtitle] = useState('')

  useEffect(() => {
    const subtitleDate = showDateSubtitle(new Date(filter.start), new Date(filter.end), daysOnInterval.length)
    setDaysFormatedToSubtitle(subtitleDate)
  }, [filter.start, filter.end])

  const {
    data: facesCountData,
    isRefetching: isRefetchingFacesCountData,
    isLoading: isLoadingFacesCountData,
  } = useGetCountingFaces({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds},
    start: filter.start,
    end: filter.end,
    offset,
    timeZone,
  })

  const {
    data: ageAndGenderData,
    isRefetching: isRefetchingAgeAndGenderData,
    isLoading: isLoadingAgeAndGenderData,
  } = useAgeAndGenderCounting({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds},
    start: filter.start,
    end: filter.end,
    offset,
    timeZone,
  })

  const {
    data: histogramData,
    isRefetching: isRefetchingHistogramData,
    isLoading: isLoadingHistogramData,
  } = useGetHistogramData({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds, range: filter.typeHistogram},
    start: filter.start,
    end: filter.end,
    offset,
    timeZone,
  })

  const {
    data: heatmapData,
    isRefetching: isRefetchingHeatmapData,
    isLoading: isLoadingHeatmapData,
  } = useGetHeatmapData({
    filter: {companyIds: [selectedCompanyId], cameraIds: filter.cameraIds, clientIds: filter.clientIds},
    start: daysOnInterval.length > 1 ? filter.start : moment(sub(new Date(filter.start), {days: 6})).format(),
    end: filter.end,
  })

  const [heatmapDataFormatted, setHeatmapDataFormatted] = useState<Array<ChartData>>([])

  function groupHeatmapData(type?: string) {
    const groupCountByWeekDays: {[dia: string]: number[]} = {}
    const data: ChartData[] = []

    heatmapData?.range.forEach((date, index) => {
      const name = capitalize(new Date(date).toLocaleString('pt-br', {weekday: 'short'}).replace('.', ''))
      if (!groupCountByWeekDays[name]) groupCountByWeekDays[name] = []
      groupCountByWeekDays[name].push(heatmapData?.total[index])
    })

    Object.entries(groupCountByWeekDays).forEach((weekData) => {
      data.push({
        name: weekData[0],
        data: type === 'average' ? weekData[1].slice(8, 24).map((total) => Number((total / daysOnInterval.length).toFixed(0))) : weekData[1].slice(8, 24),
      })
    })

    setHeatmapDataFormatted(
      data.sort((a, b) => {
        return weekOrder.indexOf(a.name) - weekOrder.indexOf(b.name)
      }),
    )
  }

  useEffect(() => {
    groupHeatmapData()
  }, [heatmapData])

  return (
    <Grid container gap={'40px'} padding={'0 20px'}>
      <Grid item container xs={12} spacing={'32px'}>
        <Grid item container xs={12} lg={windowSize.width <= 1466 ? 6 : 4}>
          <CountCardMoreInfos
            title={'Fluxo de Visitantes'}
            description='visitantes'
            value={abbreviateNumber(facesCountData?.total.today ?? 0)}
            loading={isLoadingFacesCountData}
            refetching={isRefetchingFacesCountData}
            icon={<PersonDetectedIcon />}
            filteringRangeOfDates={daysOnInterval.length > 1}
            moreInfos={[
              {
                label: 'Último dia',
                value: abbreviateNumber(facesCountData?.total.yesterday ?? 0),
                percentage: transformIntoPositiveNumber(calculateIncreasePercentage(facesCountData?.total.today ?? 0, facesCountData?.total.yesterday ?? 0)),
                trendingUp: calculateIncreasePercentage(facesCountData?.total.today ?? 0, facesCountData?.total.yesterday ?? 0) > 0,
              },
              {
                label: 'Últimos 7 dias',
                value: facesCountData?.total.last7d.toFixed(0) ?? 0,
                percentage: transformIntoPositiveNumber(calculateIncreasePercentage(facesCountData?.total.today ?? 0, facesCountData?.total.last7d ?? 0)),
                trendingUp: calculateIncreasePercentage(facesCountData?.total.today ?? 0, facesCountData?.total.last7d ?? 0) > 0,
              },
              {
                label: 'Últimos 30 dias',
                value: facesCountData?.total.last30d.toFixed(0) ?? 0,
                percentage: transformIntoPositiveNumber(calculateIncreasePercentage(facesCountData?.total.today ?? 0, facesCountData?.total.last30d ?? 0)),
                trendingUp: calculateIncreasePercentage(facesCountData?.total.today ?? 0, facesCountData?.total.last30d ?? 0) > 0,
              },
              {
                label: 'Fluxo último ano',
                value: facesCountData?.total.lastYear ?? 0,
                percentage: transformIntoPositiveNumber(calculateIncreasePercentage(facesCountData?.total.today ?? 0, facesCountData?.total.lastYear ?? 0)),
                trendingUp: calculateIncreasePercentage(facesCountData?.total.today ?? 0, facesCountData?.total.lastYear ?? 0) > 0,
              },
            ]}
            rangeInfos={{
              rangeValue: facesCountData?.total.lastYear ?? 0,
              rangeDates: showDateSubtitle(
                new Date(facesCountData?.total.range[0] ?? ''),
                new Date(facesCountData?.total.range[1] ?? ''),
                daysOnInterval.length,
              ),
              percentage: transformIntoPositiveNumber(calculateIncreasePercentage(facesCountData?.total.today ?? 0, facesCountData?.total.lastYear ?? 0)),
              trendingUp: calculateIncreasePercentage(facesCountData?.total.today ?? 0, facesCountData?.total.lastYear ?? 0) > 0,
              average: calculateAverage(facesCountData?.total.today ?? 0, daysOnInterval.length),
            }}
          />
        </Grid>
        <Grid item container xs={12} lg={windowSize.width <= 1466 ? 6 : 4}>
          <CountCardMoreInfos
            title={'Visitantes Únicos'}
            description='visitantes'
            value={abbreviateNumber(facesCountData?.unique.today ?? 0)}
            loading={isLoadingFacesCountData}
            refetching={isRefetchingFacesCountData}
            icon={<FaceDetectedIcon />}
            filteringRangeOfDates={daysOnInterval.length > 1}
            moreInfos={[
              {
                label: 'Último dia',
                value: abbreviateNumber(facesCountData?.unique.yesterday ?? 0),
                percentage: transformIntoPositiveNumber(calculateIncreasePercentage(facesCountData?.unique.today ?? 0, facesCountData?.unique.yesterday ?? 0)),
                trendingUp: calculateIncreasePercentage(facesCountData?.unique.today ?? 0, facesCountData?.unique.yesterday ?? 0) > 0,
              },
              {
                label: 'Últimos 7 dias',
                value: facesCountData?.unique.last7d.toFixed(0) ?? 0,
                percentage: transformIntoPositiveNumber(calculateIncreasePercentage(facesCountData?.unique.today ?? 0, facesCountData?.unique.last7d ?? 0)),
                trendingUp: calculateIncreasePercentage(facesCountData?.unique.today ?? 0, facesCountData?.unique.last7d ?? 0) > 0,
              },
              {
                label: 'Últimos 30 dias',
                value: facesCountData?.unique.last30d.toFixed(0) ?? 0,
                percentage: transformIntoPositiveNumber(calculateIncreasePercentage(facesCountData?.unique.today ?? 0, facesCountData?.unique.last30d ?? 0)),
                trendingUp: calculateIncreasePercentage(facesCountData?.unique.today ?? 0, facesCountData?.unique.last30d ?? 0) > 0,
              },
              {
                label: 'Fluxo último ano',
                value: facesCountData?.unique.lastYear ?? 0,
                percentage: transformIntoPositiveNumber(calculateIncreasePercentage(facesCountData?.unique.today ?? 0, facesCountData?.unique.lastYear ?? 0)),
                trendingUp: calculateIncreasePercentage(facesCountData?.unique.today ?? 0, facesCountData?.unique.lastYear ?? 0) > 0,
              },
            ]}
            rangeInfos={{
              rangeValue: facesCountData?.unique.lastYear ?? 0,
              rangeDates: showDateSubtitle(
                new Date(facesCountData?.unique.range[0] ?? ''),
                new Date(facesCountData?.unique.range[1] ?? ''),
                daysOnInterval.length,
              ),
              percentage: transformIntoPositiveNumber(calculateIncreasePercentage(facesCountData?.unique.today ?? 0, facesCountData?.unique.lastYear ?? 0)),
              trendingUp: calculateIncreasePercentage(facesCountData?.unique.today ?? 0, facesCountData?.unique.lastYear ?? 0) > 0,
              average: calculateAverage(facesCountData?.unique.today ?? 0, daysOnInterval.length),
            }}
          />
        </Grid>
        <Grid item container xs={12} lg={windowSize.width <= 1466 ? 6 : 4}>
          <CountCardMoreInfos
            title={'Fluxo de Funcionários'}
            description={`funcionários`}
            value={abbreviateNumber(facesCountData?.employees.today ?? 0)}
            loading={isLoadingFacesCountData}
            refetching={isRefetchingFacesCountData}
            icon={<EmployeesDetectedIcon />}
            filteringRangeOfDates={daysOnInterval.length > 1}
            moreInfos={[
              {
                label: 'Último dia',
                value: abbreviateNumber(facesCountData?.employees.yesterday ?? 0),
                percentage: transformIntoPositiveNumber(
                  calculateIncreasePercentage(facesCountData?.employees.today ?? 0, facesCountData?.employees.yesterday ?? 0),
                ),
                trendingUp: calculateIncreasePercentage(facesCountData?.employees.today ?? 0, facesCountData?.employees.yesterday ?? 0) > 0,
              },
              {
                label: 'Últimos 7 dias',
                value: facesCountData?.employees.last7d.toFixed(0) ?? 0,
                percentage: transformIntoPositiveNumber(
                  calculateIncreasePercentage(facesCountData?.employees.today ?? 0, facesCountData?.employees.last7d ?? 0),
                ),
                trendingUp: calculateIncreasePercentage(facesCountData?.employees.today ?? 0, facesCountData?.employees.last7d ?? 0) > 0,
              },
              {
                label: 'Últimos 30 dias',
                value: facesCountData?.employees.last30d.toFixed(0) ?? 0,
                percentage: transformIntoPositiveNumber(
                  calculateIncreasePercentage(facesCountData?.employees.today ?? 0, facesCountData?.employees.last30d ?? 0),
                ),
                trendingUp: calculateIncreasePercentage(facesCountData?.employees.today ?? 0, facesCountData?.employees.last30d ?? 0) > 0,
              },
              {
                label: 'Fluxo último ano',
                value: facesCountData?.employees.lastYear ?? 0,
                percentage: transformIntoPositiveNumber(
                  calculateIncreasePercentage(facesCountData?.employees.today ?? 0, facesCountData?.employees.lastYear ?? 0),
                ),
                trendingUp: calculateIncreasePercentage(facesCountData?.employees.today ?? 0, facesCountData?.employees.lastYear ?? 0) > 0,
              },
            ]}
            rangeInfos={{
              rangeValue: facesCountData?.employees.lastYear ?? 0,
              rangeDates: showDateSubtitle(
                new Date(facesCountData?.employees.range[0] ?? ''),
                new Date(facesCountData?.employees.range[1] ?? ''),
                daysOnInterval.length,
              ),
              percentage: transformIntoPositiveNumber(
                calculateIncreasePercentage(facesCountData?.employees.today ?? 0, facesCountData?.employees.lastYear ?? 0),
              ),
              trendingUp: calculateIncreasePercentage(facesCountData?.employees.today ?? 0, facesCountData?.employees.lastYear ?? 0) > 0,
              average: calculateAverage(facesCountData?.employees.today ?? 0, daysOnInterval.length),
            }}
          />
        </Grid>
      </Grid>

      <Grid item container xs={12} spacing={'32px'}>
        <Grid item container xs={12} lg={4}>
          <DashboardTableRowCard
            title='Gênero e Idade'
            subtitle={`Referência: Média de visitantes únicos do dia ${daysFormatedToSubtitle}`}
            loading={isLoadingAgeAndGenderData}
            refetching={isRefetchingAgeAndGenderData}
            data={[
              {
                name: 'Homens',
                icon: <MenIcon />,
                value: ageAndGenderData?.totalMale,
                percentage: calculatePercent(ageAndGenderData?.totalMale ?? 0, calculateAverage(facesCountData?.unique.today ?? 0, daysOnInterval.length)),
              },
              {
                name: 'Mulheres',
                icon: <WomenIcon />,
                value: ageAndGenderData?.totalFemale,
                percentage: calculatePercent(ageAndGenderData?.totalFemale ?? 0, calculateAverage(facesCountData?.unique.today ?? 0, daysOnInterval.length)),
              },
              {name: 'Média de idade', icon: <AgeIcon />, value: Number(ageAndGenderData?.avarageAge?.toFixed(0))},
            ]}
          />
        </Grid>
        <Grid item container xs={12} lg={8}>
          <DashboardColumnChartsCard
            title='Faixa Etária'
            subtitle={`Referência: Média de visitantes únicos do dia ${daysFormatedToSubtitle}`}
            loading={isLoadingAgeAndGenderData}
            refetching={isRefetchingAgeAndGenderData}
            data={[
              {
                name: 'Homens',
                data: ageAndGenderData?.series.find((it) => it.name === GenderEnum.MALE)?.data ?? [],
                color: '#1570EF',
              },
              {
                name: 'Mulheres',
                data: ageAndGenderData?.series.find((it) => it.name === GenderEnum.WOMAN)?.data ?? [],
                color: '#E31B54',
              },
            ]}
            labelsXaxis={AGE_GROUPS_WITH_LEGENDS}
            enableDataLabels
          />
        </Grid>
      </Grid>

      <Grid item container xs={12} spacing={'32px'}>
        <Grid item container xs={12} lg={6}>
          <DashboardColumnAndLinesChartsCard
            title='Contagem - Visitantes Únicos'
            subtitle={`Referência: Total de visitantes únicos do dia ${daysFormatedToSubtitle}`}
            data={[
              {
                data: histogramData?.total ?? [],
                name: 'Visitantes',
                type: 'column',
                color: '#1570EF',
              },
            ]}
            loading={isLoadingHistogramData}
            refetching={isRefetchingHistogramData}
            labelsXaxis={formatWeekOnLabel(histogramData?.range ?? [], filter.typeHistogram ?? '') ?? []}
            setType={(type) => onChangeFilter(type)}
          />
        </Grid>
        <Grid item container xs={12} lg={6}>
          <DashboardHeatMapTable
            title='Mapa de Calor - Visitantes Únicos'
            subtitle={`Referência: Média de visitantes únicos do dia ${daysFormatedToSubtitle}`}
            loading={isLoadingHeatmapData}
            refetching={isRefetchingHeatmapData}
            data={heatmapDataFormatted}
            labelsXaxis={heatmapData?.range.map((it) => moment(it).format('HH')).slice(8, 24) ?? []}
            setType={(type) => groupHeatmapData(type)}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}
